import { DeactivatableButton } from '@folio/components';
import { useAssertedParams } from '@folio/lib';
import { offlineAwarePaymentMethodRule } from '@folio/utils/offlineAwarePaymentMethodRule';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  brokerageFirmDtoFirmTypeEnum,
  configControllerGetPublicProductConfigQueryOptions,
  customerControllerGetCustomerQueryOptions,
  keyValueFieldsToObject,
  policyControllerConfirmPolicy,
  policyControllerGetPolicyQueryOptions,
  usePolicyControllerGeneratePolicySignatureRequest,
} from 'korint-sdk';
import { PenIcon } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  OtpPhoneNumberModal,
  type OtpPhoneNumberModalRef,
} from '../LegacyModule/OtpPhoneNumberModal';
import { useFinalizeNewBusinessRules } from '../useFinalizeNewBusinessRules';
import { useVisibilityConditionsProps } from '../useVisibilityConditionsProps';

export function PhygitalSignatureButton({ overridenBranchId }: { overridenBranchId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modalRef = useRef<OtpPhoneNumberModalRef>(null);

  const { policyId, branchId: assertedBranchId } = useAssertedParams();
  const branchId = overridenBranchId ?? assertedBranchId;
  const { policy, mutate: refetchPolicyOnDashboard } = usePolicyOnDashboard();

  const visibilityConditionsProps = useVisibilityConditionsProps({
    action: 'CONFIRM',
    overridenBranchId,
  });

  const { data: phoneCustomFieldKey } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: ({ customerFields }) =>
      customerFields.find(({ integrationKey }) => integrationKey === 'phoneNumber')?.key,
  });

  const { data: customerId } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: ({ customers }) => customers[0],
    enabled: Boolean(branchId),
  });

  const { data: customerCustomFields } = useQuery({
    ...customerControllerGetCustomerQueryOptions(customerId!, { branchId }),
    select: ({ customFields }) => keyValueFieldsToObject(customFields ?? []),
    enabled: Boolean(customerId),
  });

  const phoneNumber = phoneCustomFieldKey
    ? (customerCustomFields?.[phoneCustomFieldKey] as string)
    : '';

  const { mutateAsync: generatePolicySignatureRequest } =
    usePolicyControllerGeneratePolicySignatureRequest({
      mutation: {
        // biome-ignore lint/suspicious/noExplicitAny: typing problem for error
        retry: (failureCount: number, error: any) =>
          error?.errorResponse?.status === 425 && failureCount < 100,
        retryDelay: () => 1500,
      },
    });

  const { mutateAsync: signFaceToFace, isPending } = useMutation({
    mutationFn: async (phone: string) => {
      if (visibilityConditionsProps?.policyStatus !== 'POLICY_CONFIRMED') {
        await policyControllerConfirmPolicy(policyId, { branchId });
      }
      await generatePolicySignatureRequest({
        policyId,
        data: { deliveryMode: 'URL', isOtp: true, phoneNumber: phone },
        params: { branchId },
      });
      navigate(`/policies/${policyId}/${branchId}/phygital-signature`);
    },
    onSuccess: async () => {
      modalRef.current?.close();
      await refetchPolicyOnDashboard();
    },
  });

  const rules = useFinalizeNewBusinessRules({
    additionnalRules: [
      { id: 'card-payment', isValid: offlineAwarePaymentMethodRule({ requiredType: 'card' }) },
    ],
    overridenBranchId,
  });

  if (!rules) {
    return null;
  }

  if (!visibilityConditionsProps) {
    return null;
  }

  const showButton =
    visibilityConditionsProps.firmType === brokerageFirmDtoFirmTypeEnum.RETAIL &&
    visibilityConditionsProps.isDerogationSatisfied &&
    visibilityConditionsProps.branchStatus !== 'BRANCH_CLOSED' &&
    visibilityConditionsProps.policyStatus === 'POLICY_QUOTED';

  if (!showButton) {
    return null;
  }

  return (
    <>
      <OtpPhoneNumberModal
        ref={modalRef}
        defaultPhoneNumber={phoneNumber}
        onSend={async phone => {
          await signFaceToFace(phone);
        }}
        isLoading={isPending}
        customerPageUrl={`/policies/${policyId}/${branchId}/informations`}
      />
      <DeactivatableButton
        variant="ghost"
        rules={rules}
        icon={<PenIcon className="h-4 w-4" />}
        onClick={() => modalRef.current?.open()}
      >
        <span>{t('finalizeNewBusiness.buttons.send-embedded-signature.label')}</span>
      </DeactivatableButton>
    </>
  );
}
